import _ from 'underscore';
import $ from 'jquery';

import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { helpers, toolbarPlugin } from '@bingads-webui/projection-grid-toolbar';

import { aggregatedRowsPluginBuilder } from '@bingads-webui-campaign/pgrid-aggregated-rows';
import { performanceLogPlugin, perfMarkerPlugin } from '@bingads-webui-campaign/pgrid-performance-log';
import { columnChooserPlugin } from '@bingads-webui-campaign/projection-grid-column-chooser';
import { dateFilterPlugin } from '@bingads-webui-campaign/projection-grid-date-filter';
import { periodOverPeriodPlugin } from '@bingads-webui-campaign/projection-grid-period-over-period';
import { segmentPlugin } from '@bingads-webui-campaign/projection-grid-segment';
import { a11yPlugin } from '@bingads-webui/projection-grid-a11y';
import { assistedSelectionPlugin } from '@bingads-webui/projection-grid-assisted-selection';
import { uiBlockerPlugin } from '@bingads-webui/projection-grid-uiblocker';
import { paginationPlugin } from '@bingads-webui/projection-grid-pagination';
import { placeholderPlugin } from '@bingads-webui/projection-grid-placeholder';
import { labelsPlugin } from '@bingads-webui-campaign/projection-grid-labels';
import { Label } from '@bingads-webui-campaign/campaign-ui-schemas';
import {
  timeSegmentationTypeEnum,
  campaignSegmentationTypeEnum,
  segmentationType,
} from '@bingads-webui-campaign/segmentation-utilities';
import bamPlugin from './bam-plugin';

import { getToolbarItems } from './toolbar-config';
import { columnChooserConfiguration } from './column-chooser-config';
import { gridsMap } from '../constants';
import { isInImportTab, isInPerformanceTab, isInSmartPerformanceTab, isInManagementOrBudgetTab } from '../common-utils';
import { isInManagementTab } from '../helper/management-dashboard-helper';

const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/accounts-summary');
const schemaI18n = globalContext.require('$/schema-i18n!campaign');
const accountSchema = globalContext.require('$/schema!campaign/Account');
const globalConfig = globalContext.require('component/config/index');
const { setSegType, getSegType } = globalContext.require('component/bingads-grid/service/segmentation-preferences');
const edm = globalContext.requireDefault('component/edm-campaign/index');
const state = globalContext.require('component/state/index');
const logger = globalContext.require('module/service/instrumentation/index');
const { legacyRouter } = globalContext.require('component/legacy-router/index');
const i18nErrors = globalContext.require('component/api-errors-campaign/index');
const instrumentation = globalContext.requireDefault('campaignui/feature/instrumentation/index');
const jsdata = globalContext.require('component/js-data-campaign/index');
const router = _.clone(legacyRouter);
const cacheService = globalContext.require('Services/CacheService');
const dataContextManager = globalContext.require('SummaryDataContextManager');
const { LabelAssociationView } = globalContext.require('campaignui/feature/common-grid-plugins/label/label-association-view');

const customerHierarchyPlugin = globalContext.requireDefault('campaignui/feature/bingadsgrid/vnext/plugin/customer-hierarchy-plugin');
const inlineDownloadPlugin = globalContext.requireDefault('campaignui/feature/bingadsgrid/vnext/plugin/inline-download');
const { inlineDownloadWithoutFilterStatePlugin } = globalContext.require('campaignui/feature/bingadsgrid/vnext/plugin/inline-download');
const primaryContactBulkEditorPlugin = globalContext.requireDefault('campaignui/feature/bingadsgrid/vnext/plugin/primary-contacts-bulk-edit-plugin');
const { jsdataRawDataPlugin } = globalContext.require('campaignui/feature/common-grid-plugins/js-data-raw-data');

const {
  IsCampaignBAMEnabled,
  IsCustomerHierarchyEnabled,
  IsAccountLabelsEnabled,
} = globalConfig.get('Permissions');

const dateFilterConfiguration = ({ addDateChangeListener }) => ({
  isOdataActionSource: true,
  edm,
  state,
  addDateChangeListener,
  comparisonColumns: [
    'PeriodPerformanceMetrics',
    'PerformanceMetricsChange',
    'PerformanceMetricsChangePercentage',
  ],
});

export function getAdsPlugins({
  view,
  gridType,
  parentPerfMarker,
  preferenceConfig,
  viewport,
  addDateChangeListener,
  isForVNext,
  filterService,
  p$daterange,
  scope,
  hasUnifiedSmartAccount = false,
}) {
  const {
    pageable: {
      pageSize,
    },
    dataSource,
  } = preferenceConfig;
  const isPerformanceGrid = isInPerformanceTab(gridType);
  const isSmartPerformanceGrid = isInSmartPerformanceTab(gridType);
  const isImportGrid = isInImportTab(gridType);
  const isManagementGrid = isInManagementOrBudgetTab(gridType);
  const permissions = globalConfig.get('Permissions');
  const isManagementRequest = permissions.IsManagementDashboardEnabled && isManagementGrid;
  const { gridName } = gridsMap[gridType];

  const aggregatedRowsPlugin = aggregatedRowsPluginBuilder({
    i18n,
    keys: {
      overallI18nKey: 'AccountDashboard_Total_New',
      filteredI18nKey: 'AccountDashboard_FilteredTotal_New',
    },
    permissions,
    customConfig: {
      showSearchRow: false,
      showAudienceRow: false,
      showNativeRow: false,
      showDeletedRow: false,
    },
    filterService: isForVNext ? filterService : undefined,
  });

  const getSegmentationTypes = () => {
    if (isSmartPerformanceGrid) {
      return timeSegmentationTypeEnum;
    } else if (permissions.IsVerticalAdsReportingPhase1Enabled) {
      return [...campaignSegmentationTypeEnum, segmentationType.AdScenario];
    }

    return campaignSegmentationTypeEnum;
  };

  const plugins = _.compact([{
    // For accurate grid init latency, PerfMarkerPlugin should be the first plugin to load.
    plugin: perfMarkerPlugin,
    config: {
      appConfig: globalConfig,
      marker: parentPerfMarker,
    },
  },
  {
    plugin: dateFilterPlugin,
    config: dateFilterConfiguration({ addDateChangeListener }),
  },
  !isForVNext && {
    plugin: toolbarPlugin,
    config: helpers.singleToolbar({
      items: getToolbarItems({ view, gridType, isForVNext }),
    }),
  },
  {
    plugin: performanceLogPlugin,
    config: {
      appConfig: globalConfig,
      logger,
    },
  },
  {
    plugin: uiBlockerPlugin,
    config: {
      el: isForVNext ? viewport : $('#child-page'),
    },
  },
  {
    plugin: paginationPlugin,
    config: {
      pageSize,
      availablePageSizes: [20, 50, 100, 200],
      enableRandomPage: false,
      pageNumberLabelText: isForVNext ? '' : i18n.getString('Grid_Page_Colon'),
      pageSizeText: i18n.getString('Grid_ShowRows'),
      totalPageCountText: i18n.getString('Grid_PageOf'),
    },
  },
  {
    plugin: jsdataRawDataPlugin,
  },
  {
    plugin: assistedSelectionPlugin,
  },
  {
    plugin: aggregatedRowsPlugin,
  },
  {
    plugin: a11yPlugin,
    config: { i18n },
  },
  (isPerformanceGrid || isSmartPerformanceGrid) && {
    plugin: isForVNext ? inlineDownloadWithoutFilterStatePlugin : inlineDownloadPlugin,
    config: {
      isForVNext,
      p$daterange,
      isAvailable: true,
      schema: accountSchema,
      gridId: isForVNext ? gridName : '',
      s$i18n: schemaI18n,
      ReportEmailRule: (() => {
        if (isForVNext) {
          const ReportEmailRule = state.get('$ReportEmailRule');
          state.unset('$ReportEmailRule');
          return ReportEmailRule;
        }
        return undefined;
      })(),
    },
  },
  (isPerformanceGrid || isSmartPerformanceGrid) && {
    plugin: periodOverPeriodPlugin,
    config: { i18n },
  },
  (isPerformanceGrid || isSmartPerformanceGrid) && {
    plugin: segmentPlugin,
    config: {
      i18n,
      permissions: globalConfig.get('Permissions'),
      state,
      // Uses payload instead of query to indicate segmentation type
      isOdataActionSource: isForVNext,
      edm,
      getSegType,
      setSegType,
      cellTemplates: {
        AccountName: 'default',
      },
      segmentationTypes: getSegmentationTypes(),
    },
  },
  !isImportGrid && {
    plugin: columnChooserPlugin,
    config: columnChooserConfiguration({
      gridType, isForVNext, filterService, hasUnifiedSmartAccount,
    }),
  },
  !isImportGrid && {
    plugin: bamPlugin,
    config: {
      // We don't show bulkAccountManagement edit button in management grid toolbar after new ui pilot turn on since we have a new bulk edit button.
      enabled: IsCampaignBAMEnabled && !isInManagementTab(gridType),
    },
  },
  (isPerformanceGrid || isSmartPerformanceGrid || isManagementGrid) && IsAccountLabelsEnabled && {
    plugin: labelsPlugin,
    config: {
      isForVNext: true,
      i18n,
      i18nErrors,
      router,
      jsdata,
      LabelAssociationView,
      instrumentation,
      dataContextManager,
      cacheService,
      s$label: Label,
      edm,
      scope,
      dataSource,
      isManagementRequest,
      perfMarker: parentPerfMarker,
    },
  },
  (isPerformanceGrid || isSmartPerformanceGrid || isManagementGrid) && {
    plugin: placeholderPlugin,
    config: {
      container: '.grid-noresult',
      text: i18n.getString('Grid_NoRecords'),
      filteredContainer: '.grid-noresult',
      filteredEmptyText: i18n.getString('Grid_NoFilteredRecords'),
      filterService,
    },
  },
  IsCustomerHierarchyEnabled && {
    plugin: customerHierarchyPlugin,
    config: {
      i18n,
      isForVNext,
    },
  },
  isInManagementTab(gridType) && {
    plugin: primaryContactBulkEditorPlugin,
  }]);

  return plugins;
}
